import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { api } from '../../../services/services/APIs';
import { parseSucursalActivo } from '../../../utils/sucursalesList';
import './SucursalesPanel.css';

const getEmpresaIdFromUser = (user) => {
  if (!user || typeof user !== 'object') return '';
  const direct = [
    user.empresaId,
    user.idEmpresa,
    user.empresa_id,
    user.EmpresaID,
    user.empresaID,
    user.companyId,
  ];
  for (const value of direct) {
    const parsed = String(value ?? '').trim();
    if (parsed) return parsed;
  }
  const nested = user.empresa;
  if (nested && typeof nested === 'object') {
    const nestedVals = [
      nested.empresaId,
      nested.EmpresaID,
      nested.id,
      nested.empresa_id,
    ];
    for (const value of nestedVals) {
      const parsed = String(value ?? '').trim();
      if (parsed) return parsed;
    }
  }
  return '';
};

const readSucursales = (raw) => {
  if (!raw || typeof raw !== 'object') return [];
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw.sucursales)) return raw.sucursales;
  if (Array.isArray(raw.items)) return raw.items;
  return [];
};

export default function SucursalesPanel() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sucursales, setSucursales] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFileName, setLogoFileName] = useState('');
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const empresaId = getEmpresaIdFromUser(user);
        if (!empresaId) {
          setError('No se encontró empresaId en la sesión.');
          setLoading(false);
          return;
        }
        const res = await api.getSucursales.byEmpresa(empresaId);
        if (!cancelled) {
          if (!res.ok) {
            setError('Error al cargar las sucursales.');
            setLoading(false);
            return;
          }
          const json = await res.json();
          const list = readSucursales(json);
          setSucursales(list.map((s, idx) => ({
            key: s.SucursalID || s.sucursalId || s.id || `suc-${idx}`,
            nombre: s.NombreSucursal || s.nombre || s.nombreSucursal || 'Sin nombre',
            Correo: s.Correo || '',
            Telefono: s.Telefono || '',
            ImagenID: s.ImagenID || '',
            Activo: parseSucursalActivo(s.Activo ?? s.activo),
            Domicilio: s.Domicilio || null,
          })));
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching sucursales:', err);
        if (!cancelled) {
          setError('Error al cargar las sucursales.');
          setLoading(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [user]);

  const handleEdit = (sucursal) => {
    const domicilio = sucursal.Domicilio || {};
    setSelectedSucursal(sucursal);
    setForm({
      SucursalID: sucursal.key || '',
      NombreSucursal: sucursal.nombre || '',
      Correo: sucursal.Correo || '',
      Telefono: sucursal.Telefono || '',
      Logo: sucursal.ImagenID || '',
      Imagen: sucursal.Imagen || null,
      Calle: domicilio.Calle || '',
      NumExt: domicilio.NumExt || '',
      NumInt: domicilio.NumInt || '',
      Entre1: domicilio.Entre1 || '',
      Entre2: domicilio.Entre2 || '',
      Colonia: domicilio.Colonia || '',
      CP: domicilio.CodigoPostalID || '',
      Estado: domicilio.Estado || '',
      Pais: '',
      MetaVenta: '',
      PuntoEquilibrio: '',
      Activo: parseSucursalActivo(sucursal.Activo),
    });
    if (sucursal.Imagen && sucursal.Imagen.RutaView) {
      setLogoPreview(sucursal.Imagen.RutaView);
    }
    setShowModal(true);
    setModalError('');
    setModalSuccess('');
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSucursal(null);
    setForm({});
    setModalError('');
    setModalSuccess('');
    setLogoPreview(null);
    setLogoFileName('');
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'Telefono') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setForm(prev => ({ ...prev, [name]: numericValue }));
      }
    } else {
      setForm(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1024 * 1024) {
        alert('El archivo es demasiado grande. Máximo 1MB.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        alert('Por favor seleccione una imagen válida (PNG o JPG).');
        return;
      }

      setLogoFileName(file.name);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setLogoPreview(base64String);
        setForm(prev => ({ ...prev, Logo: base64String }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setModalError('');
    setModalSuccess('');
    try {
      const res = await api.getSucursales.updateSucursal(selectedSucursal.key, form);
      if (res.ok) {
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
          handleCloseModal();
          window.location.reload();
        }, 3000);
      } else {
        const errorData = await res.json().catch(() => ({}));
        setModalError(errorData.message || 'Error al actualizar la sucursal.');
      }
    } catch (err) {
      console.error('Error updating sucursal:', err);
      setModalError('Error al actualizar la sucursal.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <article className="config-section">
      {loading ? (
        <div className="config-feedback">
          <div className="loading-spinner"></div>
          <p>Cargando sucursales…</p>
        </div>
      ) : null}
      {error ? <p className="config-feedback config-feedback--error">{error}</p> : null}
      {!loading && !error && sucursales.length === 0 ? (
        <p className="config-feedback">No hay sucursales disponibles.</p>
      ) : null}
      {!loading && !error && sucursales.length > 0 ? (
        <div className="config-roles-table" role="table">
          <div className="config-roles-row config-roles-row--head" role="row">
            <div role="columnheader">Nombre de sucursal</div>
            <div role="columnheader">Estado (BD)</div>
            <div role="columnheader">Acciones</div>
          </div>
          {sucursales.map((item) => (
            <div key={item.key} className="config-roles-row" role="row">
              <div role="cell">{item.nombre}</div>
              <div role="cell">
                {item.Activo ? (
                  <span className="config-field-hint">Activa en catálogo</span>
                ) : (
                  <span className="config-field-hint config-field-hint--warn">
                    Inactiva en catálogo (Activo = 0)
                  </span>
                )}
              </div>
              <div role="cell">
                <button
                  type="button"
                  className="config-btn-edit"
                  onClick={() => handleEdit(item)}
                >
                  <span className="material-symbols-rounded">edit</span>
                  Editar
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {/* Modal editar sucursal */}
      {showModal && selectedSucursal && (
        <div className="dashboard-modal-backdrop" onClick={handleCloseModal}>
          <div
            className="dashboard-modal sucursales-modal-large"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="dashboard-modal-header">
              <h2>Editar Sucursal</h2>
              <button
                type="button"
                className="dashboard-modal-close"
                onClick={handleCloseModal}
              >
                ×
              </button>
            </div>
            <div className="dashboard-modal-body">
              <form onSubmit={handleSave}>
                {modalError && <p className="config-feedback config-feedback--error">{modalError}</p>}
                {modalSuccess && <p className="config-feedback config-feedback--ok">{modalSuccess}</p>}
                
                <div className="sucursal-section">
                  <h3>Datos Sucursal</h3>
                  <div className="sucursal-datos-grid">
                    <div className="sucursal-logo-section">
                      <div className="sucursal-logo-preview" onClick={handleFileButtonClick} style={{ cursor: 'pointer' }}>
                        {logoPreview || (form.Imagen && form.Imagen.RutaView) || form.Logo ? (
                          <img 
                            src={logoPreview || (form.Imagen && form.Imagen.RutaView) || form.Logo} 
                            alt="Logo sucursal" 
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="sucursal-logo-placeholder"><span class="material-symbols-rounded">store</span></div>';
                            }}
                          />
                        ) : (
                          <div className="sucursal-logo-placeholder">
                            <span className="material-symbols-rounded">store</span>
                          </div>
                        )}
                      </div>
                      <div className="sucursal-logo-input">
                        <input
                          type="text"
                          readOnly
                          value={logoFileName || 'Seleccionar logo...'}
                          placeholder="Seleccionar logo..."
                        />
                        <input 
                          ref={fileInputRef}
                          type="file" 
                          accept="image/png, image/jpeg, image/jpg" 
                          onChange={handleFileChange}
                          style={{ display: 'none' }}
                        />
                        <button type="button" className="sucursal-upload-btn" onClick={handleFileButtonClick}>
                          <span className="material-symbols-rounded">upload_file</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="sucursal-fields">
                      <label className="empresa-field empresa-field--full">
                        <span>Nombre de Sucursal*</span>
                        <input
                          name="NombreSucursal"
                          value={form.NombreSucursal || ''}
                          onChange={handleFormChange}
                          required
                        />
                      </label>
                      
                      <label className="empresa-field">
                        <span>Correo</span>
                        <input
                          type="email"
                          name="Correo"
                          value={form.Correo || ''}
                          onChange={handleFormChange}
                          placeholder="contacto@sucursal.com"
                        />
                      </label>
                      
                      <label className="empresa-field">
                        <span>Teléfono (10 dígitos)</span>
                        <input
                          type="tel"
                          name="Telefono"
                          value={form.Telefono || ''}
                          onChange={handleFormChange}
                          placeholder="9991234567"
                          pattern="[0-9]{10}"
                          maxLength="10"
                          title="Ingrese exactamente 10 dígitos numéricos"
                        />
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="sucursal-section sucursal-section--locked">
                  <h3>
                    Domicilio Sucursal
                    <div className="empresa-locked-badge">
                      <span className="material-symbols-rounded">lock</span>
                      Bloqueado
                    </div>
                  </h3>
                  <div className="empresa-grid">
                    <label className="empresa-field empresa-field--span2">
                      <span>Calle</span>
                      <input
                        name="Calle"
                        value={form.Calle || ''}
                        onChange={handleFormChange}
                        placeholder="Ej. Calle 60 por 57"
                        readOnly
                      />
                    </label>
                    
                    <label className="empresa-field">
                      <span>Número Exterior</span>
                      <input
                        name="NumExt"
                        value={form.NumExt || ''}
                        onChange={handleFormChange}
                        placeholder="S/N o Número"
                        readOnly
                      />
                    </label>
                    
                    <label className="empresa-field">
                      <span>Número Interior</span>
                      <input
                        name="NumInt"
                        value={form.NumInt || ''}
                        onChange={handleFormChange}
                        placeholder="Dpto / Local"
                        readOnly
                      />
                    </label>
                    
                    <label className="empresa-field">
                      <span>Entre</span>
                      <input
                        name="Entre1"
                        value={form.Entre1 || ''}
                        onChange={handleFormChange}
                        placeholder="Calle X"
                        readOnly
                      />
                    </label>
                    
                    <label className="empresa-field">
                      <span>y</span>
                      <input
                        name="Entre2"
                        value={form.Entre2 || ''}
                        onChange={handleFormChange}
                        placeholder="Calle Y"
                        readOnly
                      />
                    </label>
                    
                    <label className="empresa-field">
                      <span>Colonia</span>
                      <input
                        name="Colonia"
                        value={form.Colonia || ''}
                        onChange={handleFormChange}
                        placeholder="Ej. Centro"
                        readOnly
                      />
                    </label>
                    
                    <label className="empresa-field">
                      <span>Código Postal</span>
                      <input
                        name="CP"
                        value={form.CP || ''}
                        onChange={handleFormChange}
                        placeholder="97000"
                        readOnly
                      />
                    </label>
                    
                    <label className="empresa-field">
                      <span>Estado</span>
                      <input
                        name="Estado"
                        value={form.Estado || ''}
                        onChange={handleFormChange}
                        placeholder="Yucatán"
                        readOnly
                      />
                    </label>
                    
                    <label className="empresa-field">
                      <span>País</span>
                      <input
                        name="Pais"
                        value={form.Pais || ''}
                        onChange={handleFormChange}
                        placeholder="México"
                        readOnly
                      />
                    </label>
                  </div>
                </div>
                
              </form>
            </div>
            <div className="dashboard-modal-footer">
              <button
                type="button"
                className="dashboard-btn-consultar"
                onClick={handleCloseModal}
                disabled={submitting}
              >
                CANCELAR
              </button>
              <button
                type="button"
                className="dashboard-btn-consultar"
                onClick={handleSave}
                disabled={submitting}
              >
                {submitting ? 'GUARDANDO...' : 'GUARDAR'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="empresa-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="empresa-modal-success" onClick={(e) => e.stopPropagation()}>
            <div className="empresa-modal-icon">
              <span className="material-symbols-rounded">check_circle</span>
            </div>
            <h3>¡Sucursal Actualizada!</h3>
            <p>Los cambios se han guardado exitosamente</p>
            <button 
              className="empresa-modal-btn" 
              onClick={() => setShowSuccessModal(false)}
            >
              Aceptar
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
